/**
 * Text filters para normalización automática en inputs.
 *
 * Reglas del sistema ByteISP (alineadas con Bytecom prod legacy):
 *   - `stripDiacritics`  → "José" → "Jose"
 *   - `toTitleCase`      → "manuel alejandro" → "Manuel Alejandro"
 *                          con conectores en minúscula: "teresa de jesús" → "Teresa de Jesus"
 *   - `toUpperNoAccents` → "Jiménez" → "JIMENEZ"  (útil para RFC/CURP extendidos)
 *   - `toLowerTrim`      → emails / slugs
 *
 * Se aplican on-change (tiempo real) sin mover el cursor porque preservan la
 * longitud del string. Los usamos en componentes `TitleCaseInput` / `UpperInput`.
 */

/** Palabras que siempre quedan en minúscula (excepto al inicio de la cadena).
 *
 * F111 — `san` y `santa` REMOVIDOS: en español casi siempre son
 * toponimicos donde va capital (San Pedro, Santa María, Site San Pedro).
 * Si quedan en minúscula rompen nombres reales de routers/sitios/clientes.
 */
const SMALL_WORDS = new Set([
  "de", "del", "la", "las", "los", "el", "y", "e", "o", "u",
  "da", "do", "dos", "das", "van", "von", "der", "di",
]);

/** Quita tildes/diacríticos (Unicode NFD + remove combining marks). */
export function stripDiacritics(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/**
 * Title Case respetando conectores pequeños en español.
 * `terezA de JESÚS de la cruz` → `Tereza de Jesus de la Cruz`
 * No trimea — deja espacios al final para que puedas seguir escribiendo.
 */
export function toTitleCase(s: string): string {
  if (!s) return s;
  const clean = stripDiacritics(s).toLowerCase();
  // Split preservando separadores para mantener posición del cursor.
  return clean.replace(/[\p{L}\p{N}]+/gu, (word, offset: number, str: string) => {
    // ¿Es la primera palabra no-whitespace de la cadena?
    const prefix = str.slice(0, offset);
    const isFirstWord = /^\s*$/.test(prefix);
    if (!isFirstWord && SMALL_WORDS.has(word)) {
      return word;
    }
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
}

/** MAYÚSCULAS sin acentos (útil para RFC/CURP si el usuario escribe con tilde). */
export function toUpperNoAccents(s: string): string {
  return stripDiacritics(s).toUpperCase();
}

/** Minúsculas + trim (emails). */
export function toLowerTrim(s: string): string {
  return s.trim().toLowerCase();
}

/** Solo dígitos — para CP, teléfono sin formato, etc. */
export function onlyDigits(s: string): string {
  return s.replace(/\D+/g, "");
}
